import { useState } from 'react';
import styled from 'styled-components';

import { useClickAttachment } from 'hooks/useClickAttachment';
import { capitalize } from 'lib/util';
import colors from 'styles/theme/colors';
import textStyles from 'styles/theme/textStyles';
import ChevronDownSmall from 'svg/ChevronDownSmall';

export type AdmissionsCriteriaColor = 'green' | 'yellow' | 'red' | 'orange' | 'blue';

export type AdmissionSource = {
  attachmentId: string;
  displayId: number;
  // Pages of the cited document the evidence was found on, and that document's name — what the
  // referral clinical assessment's hover preview renders (OLIO-3140). Both optional: `filename` is
  // joined from the run's own packet by the caller that has one, and `pages` is empty on a criteria
  // evaluated before olio-api began recording them. Without either, the badge names the document
  // alone; the admission criteria card supplies neither and keeps its plain click-to-open chip.
  filename?: string | null;
  pages?: number[];
};

export type AdmissionsCriteriaItem = {
  // The stable criteria id, on the referral clinical assessment only — what a dismissal names.
  aiCriteriaId?: string;
  label: string;
  description?: string;
  // The AI's reviewer-facing clinical statement about this patient, and how strongly the criteria is
  // evidenced ('strong' | 'likely' | 'possible', rendered capitalized and verbatim). Both absent on
  // criteria evaluated before the evidence enrichment landed — see CriteriaEvidence.
  finding?: string;
  matchStrength?: string;
  sourceCount: number;
  sources?: AdmissionSource[];
};

export type AdmissionsCriteriaProps = {
  categoryLabel: string;
  color: AdmissionsCriteriaColor;
  criteria: AdmissionsCriteriaItem[];
};

export const colorConfig: Record<AdmissionsCriteriaColor, { accent: string; background: string }> = {
  green: {
    accent: colors.green100,
    background: colors.green5,
  },
  yellow: {
    accent: colors.yellow100,
    background: colors.yellow5,
  },
  red: {
    accent: colors.red100,
    background: colors.red5,
  },
  orange: {
    accent: colors.orange100,
    background: colors.orange5,
  },
  blue: {
    accent: colors.blue100,
    background: colors.blue5,
  },
};

export type CriteriaCategory = {
  // A stable identifier for the category, used as the React list key when present. Optional
  // because the admission-review source only carries one on snapshots written after OLIO-2697
  // (there is deliberately no backfill); the referral clinical value always does.
  id?: string;
  label: string;
  color: AdmissionsCriteriaColor;
  criteria: AdmissionsCriteriaItem[];
};

// The evidence tiers in display order, strongest first — the wire vocabulary, lowercased.
const MATCH_STRENGTH_ORDER = ['strong', 'likely', 'possible'];

// Rank a match strength for sorting: strong before likely before possible. Anything outside the
// vocabulary — including a criteria carrying no strength at all, which is every criteria evaluated
// before the evidence enrichment landed — ranks after all three, so a legacy criteria never displaces
// a graded one. Shared so a category's criteria order the same way wherever they are rendered.
export function matchStrengthRank(matchStrength: string | null | undefined): number {
  const rank = matchStrength ? MATCH_STRENGTH_ORDER.indexOf(matchStrength.toLowerCase()) : -1;
  return rank === -1 ? MATCH_STRENGTH_ORDER.length : rank;
}

// Validate an API-provided category color against the shared 5-color domain, defaulting to blue.
// Colors are configured per criteria set (not a fixed enum), so an unknown value is possible.
export function criteriaColor(color: string | null | undefined): AdmissionsCriteriaColor {
  return (color && color in colorConfig ? color : 'blue') as AdmissionsCriteriaColor;
}

// Group pre-mapped criteria under their category, preserving the first-appearance order of both
// the categories and the criteria within each. Callers order their input as needed — the
// admission job sorts by the configured order, the referral clinical value arrives pre-ordered.
export function groupCriteria(
  entries: Array<{
    categoryId?: string;
    categoryName: string;
    categoryColor: string | null | undefined;
    criteria: AdmissionsCriteriaItem;
  }>
): CriteriaCategory[] {
  const byLabel = new Map<string, CriteriaCategory>();
  for (const { categoryId, categoryName, categoryColor, criteria } of entries) {
    let category = byLabel.get(categoryName);
    if (!category) {
      category = { id: categoryId, label: categoryName, color: criteriaColor(categoryColor), criteria: [] };
      byLabel.set(categoryName, category);
    }
    category.criteria.push(criteria);
  }
  return Array.from(byLabel.values());
}

export default function AdmissionsCriteria({ categoryLabel, color, criteria }: AdmissionsCriteriaProps) {
  const { accent, background } = colorConfig[color];

  return (
    <Card>
      <ColorBar $accent={accent} />
      <Header $background={background}>
        <HeaderText>
          {criteria.length} {categoryLabel} Criteria Found
        </HeaderText>
      </Header>
      {criteria.map((criteria, index) => (
        <div key={index}>
          {index > 0 && <Divider />}
          <CriteriaItem>
            <TitleRow>
              <Title>{criteria.label}</Title>
              {criteria.sources && criteria.sources.length > 0 && (
                <SourceBadges>
                  {criteria.sources.map((source) => (
                    <CriteriaSourceBadge
                      key={source.displayId}
                      attachmentId={source.attachmentId}
                      displayId={source.displayId}
                      attachmentType='Review::Admission'
                    />
                  ))}
                </SourceBadges>
              )}
            </TitleRow>
            {criteria.description && <Description>{criteria.description}</Description>}
          </CriteriaItem>
          {/* The design rules off the evidence from the criteria body above it, full-bleed — so it
              sits outside the padded item rather than inside it. */}
          {criteria.matchStrength && (
            <>
              <Divider />
              <EvidenceItem>
                <CriteriaEvidence matchStrength={criteria.matchStrength} finding={criteria.finding} />
              </EvidenceItem>
            </>
          )}
        </div>
      ))}
    </Card>
  );
}

// A clickable citation chip — the small numbered badge beside a criteria name that opens the
// cited source document via the shared signed-URL flow. `size` scales the chip and
// `attachmentType` scopes the lookup to the owning model.
//
// Used by the admission criteria cards only. The referral clinical assessment moved to the shared
// `CitationBadge` for its hover page preview (OLIO-3140); this chip stays because the admissions
// payload carries neither a filename nor pages to preview, and that surface is not in active use.
export function CriteriaSourceBadge({
  attachmentId,
  displayId,
  attachmentType,
  size = 16,
}: {
  attachmentId: string;
  displayId: number;
  attachmentType?: string;
  size?: number;
}) {
  const openAttachment = useClickAttachment(attachmentId, attachmentType);

  return (
    <SourceBadge type='button' $size={size} onClick={openAttachment} data-testid='criteria-source'>
      {displayId}
    </SourceBadge>
  );
}

// The "Match Strength" disclosure beneath a criteria's description: a collapsed chevron row naming
// how strongly the criteria is evidenced, revealing the AI's reviewer-facing finding.
//
// Renders nothing without a strength. That is the back-compat seam AND the feature gate in one:
// criteria evaluated before this landed carry neither strength nor finding, so they fall through to
// the plain label + description card they have always rendered. Gating on the strength rather than
// the finding also keeps this card and the PDF export on one shared rule.
//
// Modelled on shared/ai/Section.tsx's chevron idiom — deliberately NOT shared/Foldout (a slide-out
// drawer with a backdrop, which is what this card sits inside) or shared/ExpandableArea (whose
// handle is a centered "Expand / Collapse" below the content).
export function CriteriaEvidence({ matchStrength, finding }: { matchStrength?: string; finding?: string }) {
  const [expanded, setExpanded] = useState(false);

  if (!matchStrength) return null;

  return (
    <EvidenceContainer>
      <EvidenceToggle
        type='button'
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
        data-testid='criteria-match-strength'>
        {/* The design's thin outlined chevron (8×5), NOT a filled caret. One component rotated
            rather than two icons, so stroke weight and size stay identical between states — the
            same approach shared/ExpandableArea takes. */}
        <EvidenceChevron $expanded={expanded} width={8} height={5} color={colors.black100} />
        <EvidenceLabel>
          Match Strength: <EvidenceStrength>{capitalize(matchStrength)}</EvidenceStrength>
        </EvidenceLabel>
      </EvidenceToggle>
      {expanded && finding && <EvidenceFinding data-testid='criteria-finding'>{finding}</EvidenceFinding>}
    </EvidenceContainer>
  );
}

// The chevron's column: its own width plus the gap to the label. The finding is indented by exactly
// this so its left edge lands on the label's text, as the design has them (both at left-369). Shared
// here rather than hand-tuned in two places, so the alignment can't drift.
const CHEVRON_WIDTH = 8;
const CHEVRON_GAP = 8;
const LABEL_INDENT = CHEVRON_WIDTH + CHEVRON_GAP;

const EvidenceContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const EvidenceToggle = styled.button`
  display: flex;
  align-items: center;
  gap: ${CHEVRON_GAP}px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  color: ${colors.black100};
`;

// Collapsed points right, expanded points down. Rotating the down-chevron counter-clockwise gives
// the right-facing state, and the fixed box keeps the label's left edge from shifting as it turns.
const EvidenceChevron = styled(ChevronDownSmall)<{ $expanded: boolean }>`
  flex: none;
  width: ${CHEVRON_WIDTH}px;
  transform: rotate(${({ $expanded }) => ($expanded ? '0deg' : '-90deg')});
  transform-origin: center;
`;

const EvidenceLabel = styled.span`
  font-family: ${textStyles.base.fontFamily};
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.15px;
  color: ${colors.black100};
`;

const EvidenceStrength = styled.span`
  font-weight: 500;
`;

// Left edge lands on the label's text, not the chevron — see LABEL_INDENT.
const EvidenceFinding = styled.p`
  margin: 12px 0 0 ${LABEL_INDENT}px;
  font-family: ${textStyles.base.fontFamily};
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.15px;
  color: ${colors.black50};
`;

const SourceBadge = styled.button<{ $size: number }>`
  min-width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 500;
  cursor: pointer;
  background: ${colors.white};
  color: ${colors.black100};
  border: 1px solid ${colors.black25};
  border-radius: 16px;
  padding: 0 2px;

  &:hover {
    background: ${colors.blue5};
    border-color: ${colors.blue100};
  }
`;

const Card = styled.div`
  position: relative;
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  overflow: hidden;
`;

const ColorBar = styled.div<{ $accent: string }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background-color: ${({ $accent }) => $accent};
  border-radius: 4px 0 0 4px;
`;

const Header = styled.div<{ $background: string }>`
  background: ${({ $background }) => $background};
  padding: 16px 16px 16px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const HeaderText = styled.p`
  margin: 0;
  font-family: ${textStyles.base.fontFamily};
  font-size: 14px;
  font-weight: 500;
  color: ${colors.black100};
`;

const CriteriaItem = styled.div`
  padding: 16px 16px 16px 24px;
`;

// Same horizontal inset as CriteriaItem so the caret lines up under the criteria text.
const EvidenceItem = styled.div`
  padding: 14px 16px 14px 24px;
`;

const Divider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid ${colors.black10};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Title = styled.p`
  margin: 0;
  font-family: ${textStyles.base.fontFamily};
  font-size: 14px;
  font-weight: 500;
  color: ${colors.black100};
`;

const SourceBadges = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
`;

const Description = styled.p`
  margin: 8px 0 0;
  font-family: ${textStyles.base.fontFamily};
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  color: ${colors.black75};
`;
